import { useState, forwardRef, useCallback } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  Typography,
  IconButton,
  Box,
  Stack,
  Slide,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

import { styles, BootstrapDialog } from "./styles";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SocialPlatforms = ({ platforms = [], onPlatformsChange = () => {} }) => {
  const { t: translation } = useTranslation("components/socialPlatforms");
  const [isSocialPlatformDialogOpen, setSocialPlatformDialogOpen] =
    useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  const groupedPlatforms = platforms.reduce((accumulator, currentValue) => {
    if (!accumulator[currentValue.category]) {
      accumulator[currentValue.category] = [];
    }

    accumulator[currentValue.category].push(currentValue);

    return accumulator;
  }, {});

  const openSocialPlatformDialog = useCallback((platform) => {
    if (platform?.accounts.length === 0) {
      setSelectedPlatform({
        ...platform,
        accounts: [{ username: "", url: "" }],
      });
    } else {
      setSelectedPlatform(platform);
    }

    setSocialPlatformDialogOpen(true);
  }, []);

  const closeSocialPlatformDialog = useCallback(() => {
    setSocialPlatformDialogOpen(false);
  }, []);

  // Helper function to handle changes in social platform account fields
  const handleSelectedPlatformChange = useCallback(
    (index, field, value) => {
      const accounts = [...selectedPlatform?.accounts];

      accounts[index] = {
        ...accounts[index],
        [field]: value,
      };

      setSelectedPlatform((prev) => ({ ...prev, accounts }));
    },
    [selectedPlatform]
  );

  // Add a new account to a specific social platform
  const addPlatformSocialAccount = useCallback(() => {
    setSelectedPlatform((prev) => ({
      ...prev,
      accounts: [...prev.accounts, { username: "", url: "" }],
    }));
  }, []);

  // Remove social platform account
  const removePlatformSocialAccount = useCallback(
    (index) => {
      if (selectedPlatform) {
        if (selectedPlatform.accounts.length > 1) {
          // If there are multiple accounts, remove the account at the specified index
          const updatedAccounts = selectedPlatform.accounts.filter(
            (_, i) => i !== index
          );

          setSelectedPlatform((prev) => ({
            ...prev,
            accounts: updatedAccounts,
          }));
        } else {
          // If there is only one account, clear its inputs
          setSelectedPlatform((prev) => ({
            ...prev,
            accounts: [{ username: "", url: "" }],
          }));
        }
      }
    },
    [selectedPlatform]
  );

  // Save social platform
  const saveSocialPlatform = useCallback(() => {
    // Check if there is only one account and its fields are empty
    const hasEmptySingleAccount =
      selectedPlatform?.accounts.length === 1 &&
      selectedPlatform.accounts[0].username.trim() === "" &&
      selectedPlatform.accounts[0].url.trim() === "";

    const updatedSocialPlatforms = platforms.map((platform) => {
      if (platform.name === selectedPlatform?.name) {
        // If there is only one empty account, set accounts to an empty array
        if (hasEmptySingleAccount) {
          return { ...platform, accounts: [] };
        } else {
          // Otherwise, save the accounts as they are
          return { ...platform, accounts: selectedPlatform.accounts };
        }
      }

      return platform;
    });

    onPlatformsChange(updatedSocialPlatforms);

    closeSocialPlatformDialog();
  }, [
    platforms,
    selectedPlatform,
    onPlatformsChange,
    closeSocialPlatformDialog,
  ]);

  return (
    <div>
      <Stack spacing={5}>
        {Object.entries(groupedPlatforms).map(([category, platforms]) => (
          <Box key={category}>
            <Typography component="h2" variant="h5">
              {translation(category)}
            </Typography>

            <Grid container spacing={3} marginTop={1}>
              {platforms.map((platform) => (
                <Grid item xs={12} sm={6} xl={4} key={platform.name}>
                  <Box
                    onClick={() => openSocialPlatformDialog(platform)}
                    style={{
                      ...styles.card,
                      background:
                        platform?.colors?.length === 1
                          ? platform?.colors[0]
                          : `linear-gradient(to right, ${platform?.colors.join(
                              ", "
                            )})`,
                    }}
                  >
                    <Box>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        color="white"
                      >
                        <platform.icon color="white" size={48} />

                        <Stack spacing={1} flex={1}>
                          <Typography variant="h6" color="white">
                            {translation(platform.name)}
                          </Typography>

                          <Button
                            variant="outlined"
                            color="inherit"
                            sx={{ borderRadius: 18 }}
                            fullWidth
                          >
                            {translation("addButton")}
                          </Button>
                        </Stack>
                      </Stack>

                      {platform?.accounts.length > 0 && (
                        <Box sx={styles.accountsCountBox}>
                          <Typography variant="h6" color="textSecondary">
                            {platform.accounts.length}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Stack>

      <BootstrapDialog
        open={isSocialPlatformDialogOpen}
        onClose={closeSocialPlatformDialog}
        fullWidth
        TransitionComponent={Transition}
      >
        <DialogTitle sx={{ m: 0, p: 2, textTransform: "capitalize" }}>
          {translation("dialogTitle", {
            accountName: selectedPlatform?.name,
          })}
        </DialogTitle>

        <Tooltip title={translation("cancelButton")}>
          <IconButton
            aria-label="close"
            onClick={closeSocialPlatformDialog}
            sx={{
              ...styles.iconButton,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>

        <DialogContent>
          {selectedPlatform?.icon && (
            <Box
              sx={{
                ...styles.iconBox,
                background:
                  selectedPlatform?.colors?.length === 1
                    ? selectedPlatform?.colors[0]
                    : `linear-gradient(to right, ${selectedPlatform?.colors.join(
                        ", "
                      )})`,
              }}
            >
              <selectedPlatform.icon color="white" size={96} />
            </Box>
          )}

          <Box sx={styles.contentBox}>
            {selectedPlatform?.accounts.map((account, index) => (
              <Stack spacing={1} key={index}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Tooltip
                    title={translation("removeButton")}
                    placement="top-start"
                  >
                    <IconButton
                      aria-label="remove-account"
                      onClick={() => removePlatformSocialAccount(index)}
                      color="error"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>

                  <Typography>
                    {translation("account") + ` ${index + 1}`}
                  </Typography>
                </Stack>

                <Stack spacing={3}>
                  <TextField
                    id={`${selectedPlatform.name}-username-${index}`}
                    fullWidth
                    label={translation("usernameLabel")}
                    variant="outlined"
                    value={account.username}
                    onChange={(e) =>
                      handleSelectedPlatformChange(
                        index,
                        "username",
                        e.target.value
                      )
                    }
                  />

                  <TextField
                    id={`${selectedPlatform.name}-url-${index}`}
                    fullWidth
                    label={translation("urlLabel")}
                    placeholder={`https://www.${selectedPlatform?.name.toLocaleLowerCase()}.com`}
                    variant="outlined"
                    value={account.url}
                    onChange={(e) =>
                      handleSelectedPlatformChange(index, "url", e.target.value)
                    }
                  />
                </Stack>
              </Stack>
            ))}

            {selectedPlatform?.accounts.length < selectedPlatform?.limit && (
              <Button
                onClick={addPlatformSocialAccount}
                variant="outlined"
                fullWidth
                size="large"
              >
                {translation("moreButton")}
              </Button>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeSocialPlatformDialog} size="large">
            {translation("cancelButton")}
          </Button>

          <Button
            onClick={saveSocialPlatform}
            variant="contained"
            color="primary"
            size="large"
          >
            {translation("submitButton")}
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </div>
  );
};

export default SocialPlatforms;
